import { ReactNode, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useGetEntity } from "../apiHooks/entities/useGetEntity";
import { useGetUsers } from "../apiHooks/users/useGetUsers";
import { useGetUserGroups } from "../apiHooks/users/useGetUserGroups";
import { getUserGroupsMembersQuery } from "../apiHooks/users/useGetUserGroupsMembers";
import {
  User,
  UserGroup,
  UserGroupsMembersQueryResult,
  UserGroupMembersIdsMap,
  NameToIdAndTypeMap,
} from "../apiHooks/users/user.types";
import { UsersAndGroupsContext } from "./UsersAndGroups.context";

type UsersAndGroupsContextProviderProps = {
  entitySubdomain?: string;
  children?: ReactNode;
};
const emptyUserList: User[] = [];
const emptyGroupList: UserGroup[] = [];
export function UsersAndGroupsContextProvider({ entitySubdomain, children }: UsersAndGroupsContextProviderProps) {
  const { data: currentEntity } = useGetEntity(entitySubdomain);
  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers(currentEntity?.entity_id);
  const { data: groupsData, isLoading: isLoadingUserGroups } = useGetUserGroups(currentEntity?.uuid);
  const users = usersData?.users || emptyUserList;
  const groups = groupsData?.groups || emptyGroupList;
  const queryClient = useQueryClient();

  const [userGroupMembersMap, setUserGroupMembersMap] = useState<UserGroupMembersIdsMap>({});

  useEffect(() => {
    groups.forEach((group) => {
      if (!group.memberCount) return;
      const membersQuery = getUserGroupsMembersQuery(group.id);
      void queryClient.fetchQuery<UserGroupsMembersQueryResult, Error>(membersQuery).then(({ members }) => {
        setUserGroupMembersMap((prev) => ({ ...prev, [group.id]: members.map((member) => member.id) }));
      });
    });
  }, [groups, queryClient]);

  const activeUsers = useMemo(() => {
    return users.filter((user) => !user.disabled);
  }, [users]);

  const groupWithMembers = useMemo(() => {
    return groups.map((group) => {
      return {
        ...group,
        userIds: userGroupMembersMap[group.id] || [],
      };
    });
  }, [groups, userGroupMembersMap]);
  const nameToIdAndTypeMap = useMemo(() => {
    const map: NameToIdAndTypeMap = {};

    // Add users to the map
    users.forEach((user) => {
      map[user.full_name.trim()] = { id: user.uuid, type: "USER" };
    });

    // Add groups to the map
    groups.forEach((group) => {
      map[group.name.trim()] = { id: group.id, type: "GROUP" };
    });

    return map;
  }, [users, groups]);

  const idToNameMap = useMemo(() => {
    return Object.fromEntries(Object.entries(nameToIdAndTypeMap).map(([name, value]) => [value.id, name]));
  }, [nameToIdAndTypeMap]);

  const usersMap = useMemo(() => {
    return users.reduce<Record<string, User | undefined>>((acc, user) => {
      acc[user.uuid] = user;
      return acc;
    }, {});
  }, [users]);

  const groupsMap = useMemo(() => {
    return groups.reduce<Record<string, UserGroup | undefined>>((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {});
  }, [groups]);

  return (
    <UsersAndGroupsContext.Provider
      value={{
        isLoadingUsers,
        isLoadingUserGroups,
        users,
        activeUsers,
        usersMap,
        nameToIdAndTypeMap,
        idToNameMap,
        groups,
        groupsMap,
        groupWithMembers,
        groupMembersMap: userGroupMembersMap,
      }}
    >
      {children}
    </UsersAndGroupsContext.Provider>
  );
}
